import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CircleNotch, Sparkle, Receipt, Storefront, ArrowLeft } from "@phosphor-icons/react";
import { Header } from "@/components/Header";
import { api, formatApiErrorDetail } from "@/lib/api";
import { RuleRow } from "@/components/receiptRules/RuleRow";
import { SuggestionTester } from "@/components/receiptRules/SuggestionTester";
import { DeleteRuleDialog } from "@/components/receiptRules/DeleteRuleDialog";

export default function ReceiptRules() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fromBusiness = searchParams.get("business") || "";
  const [payload, setPayload] = useState({ businesses: [], rules: [], counts: { item: 0, vendor: 0 } });
  const [loading, setLoading] = useState(true);
  const [businessFilter, setBusinessFilter] = useState(searchParams.get("business") || "all");
  const [kindFilter, setKindFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [catsByBiz, setCatsByBiz] = useState({});
  const [editingId, setEditingId] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/receipt-rules");
      setPayload(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't load learned rules.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { window.scrollTo({ top: 0, behavior: "auto" }); load(); }, []);

  const ensureCategories = async (businessId) => {
    if (catsByBiz[businessId]) return catsByBiz[businessId];
    const { data } = await api.get(`/businesses/${businessId}/categories`);
    setCatsByBiz((s) => ({ ...s, [businessId]: data }));
    return data;
  };

  const rules = useMemo(() => {
    const q = search.trim().toLowerCase();
    return payload.rules.filter((r) =>
      (businessFilter === "all" || r.business_id === businessFilter) &&
      (kindFilter === "all" || r.kind === kindFilter) &&
      (!q || `${r.match} ${r.merchant} ${r.category} ${r.business_name}`.toLowerCase().includes(q))
    );
  }, [payload.rules, businessFilter, kindFilter, search]);

  const saveRule = async (rule, form) => {
    if (rule.kind === "item") await api.patch(`/receipt-rules/item/${rule.id}`, { category: form.category, match: form.match });
    else await api.patch(`/categorization-rules/${rule.id}`, { vendor_name: form.match, category: form.category, txn_type: rule.txn_type });
    toast.success("Rule updated. Future suggestions use the new category; posted transactions are unchanged.");
    setEditingId("");
    await load();
  };

  const deleteRule = async (rule) => {
    if (rule.kind === "item") await api.delete(`/receipt-rules/item/${rule.id}`);
    else await api.delete(`/categorization-rules/${rule.id}`);
    toast.success("Rule deleted. Posted transactions keep their categories.");
    setPendingDelete(null);
    await load();
  };

  const testerBusiness = businessFilter !== "all" ? businessFilter : "";
  const fromBusinessName = payload.businesses.find((b) => b.id === fromBusiness)?.name || "";

  return (
    <div className="min-h-screen bg-[var(--bk-page)]">
      <Header />
      <main className="max-w-6xl mx-auto px-6 lg:px-8 py-12" data-testid="receipt-rules-page">
        <button
          onClick={() => navigate(fromBusiness ? `/business/${fromBusiness}?tab=receipts` : "/dashboard")}
          data-testid="receipt-rules-back-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border bg-white mb-6 transition-colors hover:bg-[#F6EEDF]"
          style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
        >
          <ArrowLeft size={14} weight="bold" /> {fromBusiness ? `Back to receipts${fromBusinessName ? ` · ${fromBusinessName}` : ""}` : "Back to dashboard"}
        </button>
        <div className="flex items-center gap-2">
          <Sparkle size={18} weight="fill" color="var(--bk-gold)" />
          <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--bk-accent)" }}>Receipt Rules Manager</span>
        </div>
        <h1 className="font-display text-4xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>What Rowan has learned from you</h1>
        <p className="text-sm mt-3 max-w-2xl" style={{ color: "var(--bk-text-2)" }}>
          Every time you correct a receipt line or change a vendor's category, Rowan remembers it as a rule and applies it to future suggestions.
          Editing or deleting a rule changes what Rowan suggests next — it never rewrites transactions you've already posted.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="receipt-rules-controls">
          <select value={businessFilter} onChange={(e) => setBusinessFilter(e.target.value)} data-testid="receipt-rules-business-filter" className="rounded-full border px-4 py-2.5 text-sm bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
            <option value="all">All businesses</option>
            {payload.businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <div className="inline-flex rounded-full border p-1 bg-white" style={{ borderColor: "var(--bk-border)" }} data-testid="receipt-rules-kind-filter">
            {[["all", "All rules"], ["item", "Receipt lines"], ["vendor", "Vendors"]].map(([k, label]) => (
              <button key={k} onClick={() => setKindFilter(k)} data-testid={`receipt-rules-kind-${k}`} className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors" style={kindFilter === k ? { background: "var(--bk-accent)", color: "#fff" } : { color: "var(--bk-text-2)" }}>{label}</button>
            ))}
          </div>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search description, vendor or category" data-testid="receipt-rules-search" className="flex-1 min-w-[220px] rounded-full border px-4 py-2.5 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)" }} />
          <span className="text-xs font-semibold" style={{ color: "var(--bk-text-2)" }} data-testid="receipt-rules-counts">
            <Receipt size={12} weight="bold" className="inline mr-1" />{payload.counts.item} receipt line {payload.counts.item === 1 ? "rule" : "rules"} · <Storefront size={12} weight="bold" className="inline mr-1" />{payload.counts.vendor} vendor {payload.counts.vendor === 1 ? "rule" : "rules"}
          </span>
        </div>

        <SuggestionTester businesses={payload.businesses} initialBusinessId={testerBusiness} />

        <section className="bk-card mt-6 overflow-hidden" data-testid="receipt-rules-card">
          {loading ? (
            <div className="py-14 flex items-center justify-center gap-2 text-sm" style={{ color: "var(--bk-text-2)" }} data-testid="receipt-rules-loading"><CircleNotch size={18} className="animate-spin" /> Loading learned rules…</div>
          ) : rules.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="receipt-rules-table">
                <thead>
                  <tr className="text-left border-b" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }}>
                    <th className="py-3 pl-6 pr-4 font-semibold">Matches</th>
                    <th className="py-3 pr-4 font-semibold">Suggests</th>
                    <th className="py-3 pr-4 font-semibold">Business</th>
                    <th className="py-3 pr-4 font-semibold">Learned</th>
                    <th className="py-3 pr-4 font-semibold">Used</th>
                    <th className="py-3 pr-6 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((rule) => (
                    <RuleRow key={`${rule.kind}-${rule.id}`} rule={rule} editing={editingId === rule.id} categories={catsByBiz[rule.business_id]}
                      onEdit={async () => { await ensureCategories(rule.business_id); setEditingId(rule.id); }}
                      onCancel={() => setEditingId("")} onSave={(form) => saveRule(rule, form)} onDelete={() => setPendingDelete(rule)} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-10 text-center" data-testid="receipt-rules-empty">
              <p className="font-semibold" style={{ color: "var(--bk-text)" }}>No learned rules match.</p>
              <p className="text-sm mt-2" style={{ color: "var(--bk-text-2)" }}>Correct a category on a scanned receipt line or a transaction and Rowan will remember it here.</p>
            </div>
          )}
        </section>
      </main>
      <DeleteRuleDialog rule={pendingDelete} onCancel={() => setPendingDelete(null)} onConfirm={deleteRule} />
    </div>
  );
}
